/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190902 by Jan Brunner
 *     all rights reserved.
 */

import { Injectable } from '@angular/core';
import { MaterialsDaoService } from '../materials-dao.service';
import { Material } from 'src/app/_model/material';
import {
  DbPersistenceService,
  Query,
} from 'src/app/_services/persistence/db-persistence/db-persistence.service';
import { LoggingService } from 'src/app/_services/logging/logic/logging.service';
import { AppConfig } from 'src/app/app.config';
import { KeyValuePersistenceService } from 'src/app/_services/persistence/key-value-persistence/key-value-persistence.service';
import { MaterialDbTableDefinition } from './material-db-table-definition';

// CONSTANTS
const LASTSYNCTSTAMP_KEY =
  'materials_last_sync_tstamp';
const MATERIALSONTHEMOVE_KEY =
  'materials_on_the_move';
const MATERIAL_TRANSLATE_OBJECT_KEY = "material_i18n";


@Injectable()
export class MaterialsDbDaoService implements MaterialsDaoService {

  constructor(
    private logger: LoggingService,
    private dbPersistenceService: DbPersistenceService,
    private keyValueService: KeyValuePersistenceService,
    private materialTableDefinition: MaterialDbTableDefinition,
  ) { }

  public async setLastSyncTstamp(
    tstamp: number
  ): Promise<void> {
    await this.keyValueService.store(LASTSYNCTSTAMP_KEY, tstamp);
  }
  public getLastSyncTstamp(): Promise<number> {
    return this.keyValueService.load(LASTSYNCTSTAMP_KEY);
  }

  public async saveMaterials(materials: Material[]): Promise<void> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      var materialQueries: Query[] = [];
      for (const material of materials) {
        let materialQuery: Query = {
          statement:
            'INSERT OR REPLACE INTO ' +
            this.materialTableDefinition.tableName +
            '(id, label, databag, unit, groupid, name, searchkey, extid, costcenter, wtrelevant)' + // 2
            ' VALUES (?,?,?,?,?,?,?,?,?,?)',
          parameters: [
            Number(material.getImsId()),
            material.label,
            material.databag ? JSON.stringify(material.databag) : undefined,
            material.unit,
            material.groupid,
            material.name,
            material.searchkey,
            material.extid,
            material.costcenter,
            material.wtrelevant,
          ],
        };
        materialQueries.push(materialQuery);
      }
      await this.dbPersistenceService.executeBatch(db, materialQueries);
    } catch (error) {
      this.logger.log(
        'Error saving materials to db',
        error,
        '-',
        error?.message
      );
    }
  }

  public async getMaterials(): Promise<Material[]> {
    try {
      let db = await this.dbPersistenceService.getImsDb();
      let materials: Material[] = [];

      // Get Synced Servicejobs
      let data = await this.dbPersistenceService.executeQuery(db, {
        statement: 'SELECT * FROM ' + this.materialTableDefinition.tableName + ';',
        parameters: null,
      });
      for (let i = 0; i < data.values.length; i++) {
        let row = data.values[i];
        materials.push(
          Material.fromDto({
            id: row['id'],
            secondaryid: undefined,
            label: row['label'],
            unit: row['unit'],
            groupid: row['groupid'],
            name: row['name'],
            searchkey: row['searchkey'],
            extid: row['extid'],
            costcenter: row['costcenter'],
            wtrelevant: row['wtrelevant'] === 1,
          }, row['databag'] ? JSON.parse(row['databag']) : undefined)
        );
      }
      return materials;
    } catch (error) {
      this.logger.log('Error reading materials from db', error);
      return undefined
    }
  }

  public async deleteMaterials(ids: number[]): Promise<void> {
    if (ids == undefined || ids?.length == 0) {
      return
    }

    try {
      let db = await this.dbPersistenceService.getImsDb();
      let query: Query = {
        statement: 'DELETE FROM ' + this.materialTableDefinition.tableName + ' WHERE id IN (' +ids.join(',')+ ');',
        parameters: [],
      }
      await this.dbPersistenceService.executeStatement(
        db,
        query
      )
    } catch (error) {
      this.logger.log('Error reading materials from db', error);
    }
  }


  public async saveMaterialsOnTheMove(ids: number[]): Promise<void> {
    this.keyValueService.store(MATERIALSONTHEMOVE_KEY, ids)
  } 

  public async removeMaterialsOnTheMove(): Promise<void> {
    this.keyValueService.delete(MATERIALSONTHEMOVE_KEY)
  }

  public async getMaterialsOnTheMove(): Promise<number[]> {
    return this.keyValueService.load(MATERIALSONTHEMOVE_KEY) ?? []
  }

  public async saveMaterialsTranslateObject(translateObject: any): Promise<void> {
    this.keyValueService.store(MATERIAL_TRANSLATE_OBJECT_KEY, translateObject)
  }

  public async getMaterialsTranslateObject(): Promise<any> {
    return this.keyValueService.load(MATERIAL_TRANSLATE_OBJECT_KEY)
  }

  public async reset() {
    this.logger.log(this.constructor.name, "reset lastsynctstamp")
    await this.keyValueService.delete(LASTSYNCTSTAMP_KEY)
  }

  public async clear() {
    let db = await this.dbPersistenceService.getImsDb();
    this.logger.log(this.constructor.name, "truncate table", this.materialTableDefinition.tableName)
    await this.dbPersistenceService.truncateTable(db, this.materialTableDefinition.tableName);
    await this.keyValueService.delete(LASTSYNCTSTAMP_KEY)
    await this.keyValueService.delete(MATERIALSONTHEMOVE_KEY)
    await this.keyValueService.delete(MATERIAL_TRANSLATE_OBJECT_KEY)
  }
}
